import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Building2, MapPin, ChevronRight, Truck, ClipboardCheck, Recycle, Sparkles, HelpCircle, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import EstimationBanner from '../../components/EstimationBanner';
import OptimizedImage from '../../components/OptimizedImage';
import SEO from '../../components/SEO';

const DebarrasAppartementAnnecy = () => {
  const quartiers = [
    {
      nom: 'La Vieille Ville et les quais',
      description: "Appartements de charme dans des immeubles anciens en pierre, souvent au-dessus des arcades du Thiou. Escaliers en colimaçon, portes étroites, absence totale d'ascenseur. Notre équipe protège systématiquement les murs et rampes d'époque lors de chaque passage."
    },
    {
      nom: 'Novel et Les Teppes',
      description: "Résidences collectives de grande hauteur avec ascenseurs de capacité limitée. Le volume d'encombrants par appartement est souvent important — anciens meubles, électroménager usagé. Nous coordonnons avec les gardiens pour les horaires de chargement en pied d'immeuble."
    },
    {
      nom: 'Bonlieu et centre-ville',
      description: "Copropriétés des années 70-90 autour de la gare et du centre Bonlieu. Stationnement réglementé, zones de livraison à réserver. Nous gérons les autorisations de stationnement et organisons l'évacuation en flux continu pour limiter la gêne."
    },
    {
      nom: 'Les Balmettes et la Préfecture',
      description: "Immeubles haussmanniens et résidences bourgeoises proches de la préfecture. Appartements spacieux avec parquets anciens à préserver. Les débarras de succession y sont courants, souvent avec du mobilier lourd (armoires, buffets) dans des étages élevés."
    },
    {
      nom: 'La Prairie',
      description: "Résidences calmes entre le Paquier et le lac. Beaucoup de T3-T4 dans des copropriétés bien entretenues. Les interventions concernent fréquemment des propriétaires qui revendent après plusieurs décennies et doivent vider intégralement avant la signature."
    },
    {
      nom: 'Le Parmelan et Les Romains',
      description: "Petites résidences et appartements dans des maisons divisées sur les hauteurs. L'accès se fait parfois par des voies en pente ou des impasses étroites. Nous adaptons notre logistique avec des véhicules de gabarit réduit quand la configuration l'exige."
    }
  ];

  const etapes = [
    { icon: Phone, step: '1', title: 'Premier échange', desc: 'Décrivez-nous votre appartement par téléphone ou via le formulaire : étage, ascenseur, nombre de pièces, type d\'encombrants. Nous calibrons l\'intervention.' },
    { icon: ClipboardCheck, step: '2', title: 'Visite sur place', desc: 'Un technicien visite votre appartement à Annecy pour évaluer les volumes, les contraintes d\'accès (escalier, ascenseur, parking) et rédiger un devis ferme et gratuit.' },
    { icon: ShieldCheck, step: '3', title: 'Protection des communs', desc: 'Le jour J, nous installons des protections dans les couloirs, l\'ascenseur et la cage d\'escalier. Nous respectons le règlement intérieur et les horaires du syndic.' },
    { icon: Truck, step: '4', title: 'Évacuation et tri', desc: 'Chaque objet est trié dans l\'appartement : meubles réutilisables vers les associations d\'Annecy, recyclables vers les filières locales, déchets vers les centres agréés.' },
    { icon: Sparkles, step: '5', title: 'Nettoyage final', desc: 'Appartement vidé, balayé, prêt pour l\'état des lieux, les travaux ou la remise des clés au nouveau propriétaire.' }
  ];

  const avantages = [
    {
      title: 'Spécialistes des accès difficiles',
      description: 'Escaliers étroits de la Vieille Ville, absence d\'ascenseur, paliers exigus : notre équipe est formée et équipée pour intervenir dans les configurations les plus contraignantes d\'Annecy.'
    },
    {
      title: 'Respect des parties communes',
      description: 'Nous posons des protections (bâches, couvertures, coins de protection) dans les couloirs, ascenseurs et cages d\'escalier avant chaque intervention. Aucune dégradation, aucune plainte du voisinage.'
    },
    {
      title: 'Coordination avec syndic et gardien',
      description: 'Nous contactons le syndic ou le gardien de votre résidence en amont pour réserver l\'ascenseur, planifier le créneau de chargement et respecter les horaires autorisés.'
    },
    {
      title: 'Sous 48h pour les urgences locatives',
      description: 'Fin de bail imminente, vente signée, relogement rapide : nous intervenons sous 48 heures à Annecy pour vider l\'appartement dans les temps. Le devis est remis sous 24h.'
    }
  ];

  const faq = [
    {
      question: 'Mon appartement est au 4e étage sans ascenseur dans la Vieille Ville d\'Annecy, est-ce un problème ?',
      answer: 'Pas du tout, c\'est notre quotidien. Nous intervenons régulièrement dans les immeubles anciens du Vieil Annecy. Notre équipe utilise des sangles de portage, des diables monte-escalier et des couvertures de protection. Le surcoût lié aux étages est systématiquement intégré au devis initial — pas de mauvaise surprise.'
    },
    {
      question: 'Combien coûte le débarras d\'un appartement à Annecy ?',
      answer: 'Le tarif dépend du nombre de pièces, du volume d\'encombrants et de l\'accessibilité (étage, ascenseur). Pour un T2-T3 à Annecy, comptez entre 400 et 1 200 euros. Un T4-T5 très encombré peut aller jusqu\'à 2 000 euros. Le devis est toujours gratuit et sans engagement.'
    },
    {
      question: 'Faut-il prévenir le syndic de la copropriété avant l\'intervention ?',
      answer: 'C\'est recommandé. Si vous nous le demandez, nous contactons directement le syndic ou le gardien pour réserver l\'ascenseur et convenir d\'un créneau de chargement en bas de l\'immeuble. Cela évite tout conflit avec le voisinage.'
    },
    {
      question: 'Pouvez-vous intervenir un week-end ou en soirée pour ne pas gêner les voisins ?',
      answer: 'Nous privilégions les interventions en semaine pendant les heures autorisées par le règlement de copropriété (généralement 8h-19h). Cependant, pour les cas urgents, nous pouvons organiser des interventions le samedi matin sur demande.'
    },
    {
      question: 'Videz-vous aussi la cave et le garage de l\'immeuble en même temps ?',
      answer: 'Oui. Nous débarrassons l\'appartement et toutes les annexes rattachées (cave, box, grenier, local poussette) en une seule intervention. Tout est inclus dans le même devis.'
    },
    {
      question: 'Je suis locataire et je dois rendre l\'appartement vide rapidement, que faire ?',
      answer: 'Contactez-nous dès que possible avec la date de fin de bail. Nous pouvons intervenir sous 48 heures à Annecy. Pour un appartement standard, l\'intervention dure entre 2 et 4 heures. Vous récupérez un logement vidé et balayé, prêt pour l\'état des lieux de sortie.'
    }
  ];

  const communesVoisines = [
    { nom: 'Cran-Gevrier' },
    { nom: 'Seynod' },
    { nom: 'Meythet' },
    { nom: 'Annecy-le-Vieux' },
    { nom: 'Sevrier' },
    { nom: 'Saint-Jorioz' },
    { nom: 'Veyrier-du-Lac' },
    { nom: 'Menthon-Saint-Bernard' },
    { nom: 'Poisy' },
    { nom: 'Argonay' },
    { nom: 'Epagny-Metz-Tessy' },
    { nom: 'Pringy' },
    { nom: 'Talloires-Montmin' }
  ];

  const jsonLdLocalBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://debeco.fr/#organization",
    "name": "DEBECO - Débarras & Recyclage",
    "description": "Débarras d'appartement professionnel à Annecy. Intervention même sans ascenseur, protection des parties communes, tri et recyclage.",
    "url": "https://debeco.fr/debarras-appartement-annecy",
    "telephone": "+33451401617",
    "email": "contact@debeco.fr",
    "image": "https://debeco.fr/images/annecy-immeubles-canal.jpg",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Annecy",
      "addressRegion": "Haute-Savoie",
      "postalCode": "74000",
      "addressCountry": "FR"
    },
    "areaServed": {
      "@type": "City",
      "name": "Annecy",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Haute-Savoie"
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Débarras d'appartement",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Débarras d'appartement à Annecy",
            "description": "Vidage complet d'appartement à Annecy : tri, évacuation, recyclage, protection des communs et remise en état."
          }
        }
      ]
    },
    "priceRange": "€€"
  };

  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://debeco.fr/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://debeco.fr/services" },
      { "@type": "ListItem", "position": 3, "name": "Débarras d'appartement", "item": "https://debeco.fr/services/appartement" },
      { "@type": "ListItem", "position": 4, "name": "Annecy", "item": "https://debeco.fr/debarras-appartement-annecy" }
    ]
  };

  const jsonLdFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  };

  useEffect(() => {
    const scripts = [jsonLdLocalBusiness, jsonLdBreadcrumb, jsonLdFaq].map(data => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      return script;
    });
    return () => scripts.forEach(s => s.remove());
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Débarras d'appartement à Annecy (74) | DEBECO"
        description="Débarras d'appartement à Annecy, même sans ascenseur. Devis gratuit, intervention sous 48h, protection des parties communes. Tél : 04 51 40 16 17."
        canonical="/debarras-appartement-annecy"
        ogImage="/images/annecy-immeubles-canal.jpg"
      />

      {/* Breadcrumb */}
      <nav className="bg-gray-50 border-b" aria-label="Fil d'Ariane" data-testid="breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <ol className="flex items-center text-sm text-gray-600 flex-wrap gap-y-1">
            <li><Link to="/" className="hover:text-emerald-600">Accueil</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li><Link to="/services" className="hover:text-emerald-600">Services</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li><Link to="/services/appartement" className="hover:text-emerald-600">Débarras d'appartement</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li className="text-blue-900 font-medium">Annecy</li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="local-h1">Débarras d'appartement à Annecy</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
            Vidage complet de votre appartement annécien, même sans ascenseur. Protection des parties communes garantie, tri et recyclage sur place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-xl text-lg px-8 py-6 w-full sm:w-auto" data-testid="hero-cta-devis">
                Demander un devis gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto" data-testid="hero-cta-phone">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Section: Notre service */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage
                  src="/images/annecy-immeubles-canal.jpg"
                  webpSrc="/images/annecy-immeubles-canal.webp"
                  alt="Façades colorées d'immeubles le long du canal du Thiou à Annecy, quartiers typiques où DEBECO intervient pour le débarras d'appartement"
                  className="w-full h-full object-cover"
                  width="1200"
                  height="1800"
                  loading="eager"
                  data-testid="local-appartement-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <Building2 className="w-10 h-10 text-blue-900" />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Vider un appartement à Annecy : notre savoir-faire</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Le parc immobilier annécien se compose en grande partie d'appartements : résidences du centre-ville, copropriétés des années 70 à Novel, immeubles anciens de la Vieille Ville ou résidences récentes du secteur Bonlieu. Chaque configuration pose ses propres défis lors d'un débarras.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Étage élevé sans ascenseur, cage d'escalier étroite, règlement de copropriété contraignant, voisins à préserver — notre équipe gère ces paramètres au quotidien. Avant chaque intervention, nous visitons l'appartement pour identifier les accès, mesurer les passages et planifier l'évacuation sans accroc.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  Le marché locatif d'Annecy est l'un des plus tendus de France. Que vous soyez propriétaire pressé par une vente, locataire en fin de bail ou héritier d'un appartement à vider, nous intervenons dans des délais serrés — souvent sous 48 heures — pour vous libérer de cette charge.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto">
                      Demander un devis gratuit
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0451401617">
                    <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white w-full sm:w-auto">
                      <Phone className="mr-2 w-4 h-4" />
                      04 51 40 16 17
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Quartiers */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-4 text-center">Où intervenons-nous pour un débarras d'appartement à Annecy ?</h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
              Chaque quartier a ses immeubles, ses contraintes d'accès et ses habitudes de copropriété. Nous adaptons notre méthode à chaque situation.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quartiers.map((q, index) => (
                <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <h3 className="font-bold text-lg text-blue-900">{q.nom}</h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{q.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Estimation Banner */}
      <EstimationBanner />

      {/* Section: Comment ça se passe */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-10 text-center">Les étapes d'un débarras d'appartement à Annecy</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {etapes.map((e) => (
                <div key={e.step} className="text-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <e.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">Étape {e.step}</div>
                  <h3 className="font-bold text-blue-900 mb-2">{e.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{e.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section: Pourquoi choisir DEBECO */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-10 text-center">Pourquoi faire appel à DEBECO pour votre appartement ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {avantages.map((a, index) => (
                <div key={index} className="flex items-start gap-4 p-6 bg-white rounded-xl shadow-sm border-2 border-transparent hover:border-emerald-500 transition-all">
                  <CheckCircle className="w-7 h-7 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">{a.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{a.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section: Zones */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative h-80 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage
                  src="/images/annecy.jpg"
                  webpSrc="/images/annecy.webp"
                  alt="Panorama du lac d'Annecy depuis les hauteurs, zone d'intervention DEBECO pour le débarras d'appartement"
                  className="w-full h-full object-cover"
                  width="1200"
                  height="800"
                  loading="lazy"
                  data-testid="local-annecy-image"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Communes voisines desservies</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Notre service de débarras d'appartement couvre l'ensemble du bassin annécien. Les mêmes tarifs s'appliquent quelle que soit la commune — seul le volume et l'accessibilité déterminent le prix.
                </p>
                <div className="flex flex-wrap gap-2">
                  {communesVoisines.map((c, index) => (
                    <span key={index} className="bg-blue-100 text-blue-900 px-3 py-1.5 rounded-full text-sm font-medium">
                      {c.nom}
                    </span>
                  ))}
                </div>
                <div className="mt-6">
                  <Link to="/zones/annecy" className="text-emerald-600 hover:text-emerald-700 font-medium inline-flex items-center">
                    Voir toute notre zone d'intervention Annecy
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-10">
              <HelpCircle className="w-8 h-8 text-emerald-600" />
              <h2 className="text-3xl font-bold text-blue-900">Questions fréquentes — Appartement</h2>
            </div>
            <div className="space-y-4">
              {faq.map((item, index) => (
                <Card key={index} className="border-2 hover:border-emerald-500 transition-all">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg text-blue-900 mb-3">{item.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Un appartement à vider à Annecy ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez DEBECO pour un devis gratuit sous 24h. Nous intervenons dans tous les quartiers d'Annecy, même sans ascenseur.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto" data-testid="cta-devis-final">
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto" data-testid="cta-phone-final">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DebarrasAppartementAnnecy;
